import React, { useEffect, useState } from "react";
import { sortingFns } from "@tanstack/react-table";
import { compareItems } from "@tanstack/match-sorter-utils";
import { format, parseISO } from "date-fns";

import MasterTable from "../../../components/master/MasterTable";
import ComponentLoader from "../../../components/ui/pageLoader/ComponentLoader";

import { contractAddIcon, downloadIcon } from "../../../assetData/icons";
import { actionPerm } from "../../../components/authNavigation/AuthNavbar";
import { FaSortDown, FaSortUp } from "react-icons/fa";
import { sortData } from "../../../utils/sortData";
import { downloadLoader } from "../../../assetData/loaders";

const fuzzySort = (rowA, rowB, columnId) => {
  let dir = 0;

  // Only sort by rank if the column has ranking information
  if (rowA.columnFiltersMeta[columnId]) {
    dir = compareItems(
      rowA.columnFiltersMeta[columnId]?.itemRank,
      rowB.columnFiltersMeta[columnId]?.itemRank,
    );
  }

  // Provide an alphanumeric fallback for when the item ranks are equal
  return dir === 0 ? sortingFns.alphanumeric(rowA, rowB, columnId) : dir;
};

const MasterPriceTable = (props) => {
  const {
    tableData,
    buttonConfig,
    setIsAdd,
    setIsEdit,
    setIsDelete,
    setIsImport,
    setSelectedItemId,
    renderKey,
    isLoading,
    setGlobalFilters,
    loading,
    actionPermissions,
    isDownloadClicked,
  } = props;

  const [tableFilter, setTableFilter] = useState(tableData);
  const [sortingType, setSortingType] = useState("asc");
  const [sortColumn, setSortColumn] = useState("name");

  useEffect(() => {
    setTableFilter(tableData);
  }, [tableData]);

  const handleSorting = (column, sortType, type) => {
    setSortColumn(column);

    const sortedData = sortData({
      data: tableData,
      column,
      type,
      sortType,
    });

    setTableFilter(sortedData);
    setSortingType(sortingType === "asc" ? "desc" : "asc");
  };

  const columns = React.useMemo(
    () => [
      {
        accessorKey: "sl",
        header: () => <div className="inline-block w-max pl-2">S. NO.</div>,
        cell: (info) => <div>{info.row.index + 1}.</div>,
      },

      {
        accessorFn: (row) => row.item_description,
        id: "item_description",
        cell: (info) => info.getValue(),
        header: () => (
          <div
            className="flex items-center justify-between pl-1"
            onClick={() => {
              handleSorting(
                "item_description",
                sortingType === "asc" ? "desc" : "asc",
                "string",
              );
            }}
          >
            <div>item description</div>
            <div className="space-y-[-10px]">
              <FaSortUp
                className={` ${
                  sortColumn === "item_description" && sortingType === "asc"
                    ? "opacity-100"
                    : "opacity-50"
                }`}
              />
              <FaSortDown
                className={`${
                  sortColumn === "item_description" && sortingType === "desc"
                    ? "opacity-100"
                    : "opacity-50"
                }`}
              />
            </div>
          </div>
        ),
        filterFn: "fuzzy",
        sortingFn: fuzzySort,
      },

      {
        accessorKey: "short_name",
        header: () => (
          <div
            onClick={() => {
              handleSorting(
                "short_name",
                sortingType === "asc" ? "desc" : "asc",
                "string",
              );
            }}
            className="flex w-full items-center justify-between pl-1"
          >
            <div>SHORT NAME</div>
            <div className="space-y-[-10px]">
              <FaSortUp
                className={` ${
                  sortColumn === "short_name" && sortingType === "asc"
                    ? "opacity-100"
                    : "opacity-50"
                }`}
              />
              <FaSortDown
                className={`${
                  sortColumn === "short_name" && sortingType === "desc"
                    ? "opacity-100"
                    : "opacity-50"
                }`}
              />
            </div>{" "}
          </div>
        ),
        cell: (info) => <div> {capitalizeFirstLetter(info.getValue())} </div>,
      },

      {
        accessorKey: "unit_price",
        header: () => (
          <div
            onClick={() => {
              handleSorting(
                "unit_price",
                sortingType === "asc" ? "desc" : "asc",
                "number",
              );
            }}
            className="flex items-center justify-end "
          >
            <div>unit price</div>
            <div className="space-y-[-10px]">
              <FaSortUp
                className={` ${
                  sortColumn === "unit_price" && sortingType === "asc"
                    ? "opacity-100"
                    : "opacity-50"
                }`}
              />
              <FaSortDown
                className={`${
                  sortColumn === "unit_price" && sortingType === "desc"
                    ? "opacity-100"
                    : "opacity-50"
                }`}
              />
            </div>
          </div>
        ),
        cell: (info) => (
          <div className="flex justify-end"> {info.getValue()} </div>
        ),
      },

      {
        accessorKey: "item_type",
        header: () => (
          <div
            onClick={() => {
              handleSorting(
                "item_type",
                sortingType === "asc" ? "desc" : "asc",
                "string",
              );
            }}
            className="flex items-center justify-between pl-1"
          >
            <div>item type</div>
            <div className="space-y-[-10px]">
              <FaSortUp
                className={` ${
                  sortColumn === "item_type" && sortingType === "asc"
                    ? "opacity-100"
                    : "opacity-50"
                }`}
              />
              <FaSortDown
                className={`${
                  sortColumn === "item_type" && sortingType === "desc"
                    ? "opacity-100"
                    : "opacity-50"
                }`}
              />
            </div>
          </div>
        ),
        cell: (info) => <div>{capitalizeFirstLetter(info.getValue())}</div>,
      },

      {
        accessorKey: "eff_start_date",
        header: () => (
          <div
            onClick={() => {
              handleSorting(
                "eff_start_date",
                sortingType === "asc" ? "desc" : "asc",
                "date",
              );
            }}
            className="flex items-center justify-between pl-1"
          >
            <div>start date</div>
            <div className="space-y-[-10px]">
              <FaSortUp
                className={` ${
                  sortColumn === "eff_start_date" && sortingType === "asc"
                    ? "opacity-100"
                    : "opacity-50"
                }`}
              />
              <FaSortDown
                className={`${
                  sortColumn === "eff_start_date" && sortingType === "desc"
                    ? "opacity-100"
                    : "opacity-50"
                }`}
              />
            </div>
          </div>
        ),
        cell: (info) => (
          <div className="inline-block w-max">
            {format(new Date(info.getValue()), "dd MMM yyyy")}
          </div>
        ),
      },

      {
        accessorKey: "eff_end_date",
        header: () => (
          <div
            onClick={() => {
              handleSorting(
                "eff_end_date",
                sortingType === "asc" ? "desc" : "asc",
                "date",
              );
            }}
            className="flex items-center justify-between pl-1"
          >
            <div>end date</div>
            <div className="space-y-[-10px]">
              <FaSortUp
                className={` ${
                  sortColumn === "eff_end_date" && sortingType === "asc"
                    ? "opacity-100"
                    : "opacity-50"
                }`}
              />
              <FaSortDown
                className={`${
                  sortColumn === "eff_end_date" && sortingType === "desc"
                    ? "opacity-100"
                    : "opacity-50"
                }`}
              />
            </div>
          </div>
        ),
        cell: (info) => (
          <div className="inline-block w-max">
            {format(new Date(info.getValue()), "dd MMM yyyy")}
          </div>
        ),
      },

      {
        accessorKey: "status",
        header: () => <div className="pl-1">status</div>,
        cell: (info) => <div>{info.getValue()}</div>,
      },

      {
        accessorKey: "id",
        header: () => <div> ACTIONS </div>,
        cell: (info) => <div> </div>,
      },
    ],
    [sortColumn, sortingType, tableData, tableFilter],
  );

  const handleDownloadButtonClick = () => {
    buttonConfig.downloadHandler();
  };

  const actionConfig = [
    {
      id: 3,
      name: "Add Pricing",
      image: contractAddIcon,
      actionHandler: () => setIsAdd(true),
      disable: actionPerm(actionPermissions)?.add_perm,
    },

    {
      id: 4,
      name: "Download Data",
      image: isDownloadClicked ? downloadLoader : downloadIcon,
      actionHandler: isDownloadClicked
        ? () => {}
        : buttonConfig.downloadHandler,
      disable: actionPerm(actionPermissions)?.download_perm,
    },
  ];

  function capitalizeFirstLetter(str) {
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  const action = actionPerm(actionPermissions);

  // if (!tableData) return null;
  let name = "Add Pricing";

  return (
    <MasterTable
      name={name}
      columns={columns}
      tableData={tableFilter?.length > 0 ? tableFilter : tableData}
      buttonConfig={buttonConfig}
      setIsEdit={setIsEdit}
      setIsDelete={setIsDelete}
      setSelectedItemId={setSelectedItemId}
      renderKey={renderKey}
      filterField
      actionConfig={actionConfig}
      isLoading={isLoading}
      setGlobalFilters={setGlobalFilters}
      handleDownloadButtonClick={handleDownloadButtonClick}
      loading={loading}
      actions={action}
    />
  );
};

export default MasterPriceTable;
